/**
 * Recovery from a failed boot (issue #401).
 *
 * After a release, a browser with the previous service worker runs the
 * previous bundle out of the precache while the tenant config on Firestore is
 * already current. A reload fixes it: by then the new service worker has taken
 * over and serves the new `index.html`. This module does that reload on the
 * user's behalf, once, and asks the service worker to update first.
 *
 * Loop guard: the reload is claimed in sessionStorage with a timestamp. A
 * second failure inside the window shows the error screen and stops there,
 * so a boot that keeps failing never turns into a reload loop. The stored
 * value is a timestamp only, no tenant data (apps/web CLAUDE.md rule 6).
 */

export const AUTO_RELOAD_KEY = "praesto.bootAutoReloadAt";
export const AUTO_RELOAD_WINDOW_MS = 5 * 60_000;
const SW_UPDATE_TIMEOUT_MS = 4_000;

type GuardStorage = Pick<Storage, "getItem" | "setItem">;

/**
 * Returns true when an automatic reload is allowed now, and records it.
 * Without a usable storage it returns false: no record means no guard, and
 * without a guard the safe answer is not to reload.
 */
export function claimAutoReload(storage: GuardStorage | null, now: number): boolean {
  if (!storage) return false;
  try {
    const last = Number(storage.getItem(AUTO_RELOAD_KEY));
    if (Number.isFinite(last) && last > 0 && now - last < AUTO_RELOAD_WINDOW_MS) return false;
    storage.setItem(AUTO_RELOAD_KEY, String(now));
    return true;
  } catch {
    return false;
  }
}

/**
 * Asks the service worker to check for a new version and waits (bounded) for
 * a new one to activate. Never throws: every failure just ends the wait.
 */
export async function updateServiceWorker(
  container: Pick<ServiceWorkerContainer, "getRegistration"> | undefined,
  timeoutMs: number = SW_UPDATE_TIMEOUT_MS,
): Promise<void> {
  if (!container) return;
  try {
    const registration = await container.getRegistration();
    if (!registration) return;
    await withTimeout(registration.update(), timeoutMs);
    const incoming = registration.installing ?? registration.waiting;
    if (!incoming) return;
    await withTimeout(
      new Promise<void>((resolve) => {
        if (incoming.state === "activated") return resolve();
        incoming.addEventListener("statechange", () => {
          if (incoming.state === "activated" || incoming.state === "redundant") resolve();
        });
      }),
      timeoutMs,
    );
  } catch {
    // Offline, unsupported, or the update itself failed: reload anyway.
  }
}

export interface RecoveryDeps {
  readonly storage: GuardStorage | null;
  readonly now: number;
  readonly serviceWorker: Pick<ServiceWorkerContainer, "getRegistration"> | undefined;
  readonly reload: () => void;
}

/**
 * Tries one automatic reload after a boot error. Returns true if it reloaded,
 * false if the loop guard stopped it (the error screen stays, with its button).
 */
export async function recoverFromBootError(deps: RecoveryDeps): Promise<boolean> {
  if (!claimAutoReload(deps.storage, deps.now)) return false;
  await updateServiceWorker(deps.serviceWorker);
  deps.reload();
  return true;
}

/** Live browser dependencies for `recoverFromBootError`. */
export function browserRecoveryDeps(): RecoveryDeps {
  let storage: GuardStorage | null = null;
  try {
    storage = window.sessionStorage;
  } catch {
    storage = null;
  }
  return {
    storage,
    now: Date.now(),
    serviceWorker: "serviceWorker" in navigator ? navigator.serviceWorker : undefined,
    reload: () => window.location.reload(),
  };
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T | undefined> {
  return Promise.race([
    promise,
    new Promise<undefined>((resolve) => setTimeout(() => resolve(undefined), ms)),
  ]);
}
